import { useMemo, useState } from "react";
import AppointmentTable from "../../appointments/components/AppointmentTable";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { Flex } from "@chakra-ui/react";
import Pagination from "../../../components/pagination";
import AppointmentUtils from "../../appointments/components/AppointmentUtils";
import ScheduleAppointmentModal from "../../appointments/components/ScheduleAppointmentModal";
import { useQueryClient } from "@tanstack/react-query";
import NoItemsCard from "./NoPatientsCard";
import { useParams } from "react-router-dom";
import { useGetPatientDetails } from "../../../apis/patients";

const PatientAppointment = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, _setLimit] = useState(5);
  const [isOpen, setIsOpen] = useState(false);

  const [appointmentStatus, setAppointmentStatus] = useState("All");
  const [appType, setAppType] = useState<{
    category: string | null;
    mode: string | null;
  }>({
    category: null,
    mode: null,
  });
  const { id } = useParams();

  // const { data: patientDetailsData, isLoading } = useGetPatientDetails({
  //   patientId: id as string,
  // });

  const { data: appointments, isLoading } = useGetPatientDetails({
    patientId: id as string,
  });

  const queryClient = useQueryClient();

  const cachedData: any = queryClient.getQueryData(["getPatientDetails", id]);

  const allAppointments = useMemo(() => {
    return appointments?.data?.appointments ?? [];
  }, [appointments]);

  const appsToShow = useMemo(() => {
    if (!allAppointments) return [];

    let filtered = allAppointments;

    return filtered
      .filter(
        (app: any) =>
          appointmentStatus === "All" ||
          appointmentStatus === "" ||
          app?.appointmentStatus === appointmentStatus
      )
      .filter(
        (app: any) =>
          appType.category === null ||
          app?.appointmentType?.category === appType.category
      )
      .filter(
        (app: any) =>
          appType.mode === null || app?.appointmentType?.mode === appType.mode
      )
      .map((app: any) => ({
        ...app,
        appointmentType: {
          category: app?.appointmentType?.category || "Initial",
          mode: app?.appointmentType?.mode || "Online",
        },
        status: app?.appointmentStatus || "Active",
      }));
  }, [allAppointments, appointmentStatus, appType]);

  const paginatedApps = useMemo(() => {
    const start = (currentPage - 1) * limit;
    const end = start + limit;
    return appsToShow.slice(start, end);
  }, [appsToShow, currentPage, limit]);

  const totalPages = Math.ceil(appsToShow.length / limit);

  return (
    <Flex direction={"column"} gap={"20px"}>
      {isLoading && !allAppointments.length ? (
        <p>Loading appointments...</p>
      ) : !isLoading &&
        (cachedData?.data?.total === 0 || allAppointments.length === 0) &&
        appsToShow.length === 0 ? (
        <Flex flex={1} h={"full"}>
          <NoItemsCard
            btnText="Schedule New Appointment"
            handleBtnClick={() => setIsOpen(true)}
            subtitle="This patient does not have any appointments yet"
            title="No appointments"
          />
        </Flex>
      ) : (
        <>
          <AppointmentUtils
            setPage={setCurrentPage}
            appType={appType}
            setAppType={setAppType}
            appointmentStatus={appointmentStatus}
            setAppointmentStatus={setAppointmentStatus}
            px="0px" // This prop is defined in AppointmentUtils
          />
          {appsToShow.length === 0 && !isLoading ? (
            <Flex justifyContent="center" alignItems="center" py={10}>
              <p>No appointments match the current filters.</p>
            </Flex>
          ) : (
            <AppointmentTable
              appointments={paginatedApps}
              loadingAppointments={isLoading}
              totalAppointments={appsToShow.length}
              hasActions={false}
              reference="patient"
            />
          )}
        </>
      )}

      {totalPages > 1 && (
        <div className="flex flex-1 justify-end mr-2">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      <ScheduleAppointmentModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Schedule New Appointment"
      />
    </Flex>
  );
};

export default PatientAppointment;
